"use client";

import React, { useState, useEffect } from "react";
import { Trophy, Award, Globe, ArrowRight, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
type Lang = "vi" | "en";
interface TrackPillar {
  id: string;
  title: Record<Lang, string>;
  description: Record<Lang, string>;
}
const achievementTracks: TrackPillar[] = [{
  id: "track-1",
  title: {
    vi: "Đấu trường Quốc gia",
    en: "National Leagues"
  },
  description: {
    vi: "Chương trình huấn luyện định hướng các bảng đấu FIRST LEGO League và VEX Robotics chính thức.",
    en: "Comprehensive preparation aligned with official FIRST LEGO League and VEX Robotics divisions."
  }
}, {
  id: "track-2",
  title: {
    vi: "Bản lĩnh Kỹ thuật",
    en: "Technical Rigor"
  },
  description: {
    vi: "Làm chủ cơ cấu tự hành, tinh chỉnh vòng lặp cảm biến và xử lý sự cố cơ khí theo thời gian thực.",
    en: "Hands-on mastery of autonomous mechanisms, sensor telemetry loops, and real-time troubleshooting."
  }
}, {
  id: "track-3",
  title: {
    vi: "Chuẩn mực Toàn cầu",
    en: "Global Benchmarks"
  },
  description: {
    vi: "Cọ xát tại các diễn đàn công nghệ quốc tế, trui rèn tinh thần đồng đội và tác phong kỹ sư chuyên nghiệp.",
    en: "Engagement with international youth STEM arenas, cultivating teamwork and professional engineering standards."
  }
}];
const content: Record<string, Record<Lang, string>> = {
  specRail: {
    vi: "ĐẶC TẢ: PHÒNG VINH DANH THÀNH TÍCH",
    en: "SPEC: RECOGNITION VAULT"
  },
  imgAlt: {
    vi: "Tủ cúp vinh danh chính thức và các chứng nhận giải đấu tại Học viện ROBOTO",
    en: "ROBOTO Academy official tournament trophy cabinet and framed competition certifications"
  },
  certDivision: {
    vi: "Hạng mục Thi đấu",
    en: "Tournament Division"
  },
  certCertified: {
    vi: "Chứng nhận Giải đấu Quốc gia",
    en: "National League Certified"
  },
  certVerified: {
    vi: "XÁC THỰC",
    en: "VERIFIED"
  },
  accreditation: {
    vi: "Hệ thống mô hình Robotics thi đấu đạt chuẩn theo định chuẩn STEM quốc tế",
    en: "Standardized competition robotics rigs calibrated to global STEM standards"
  },
  eyebrow: {
    vi: "Thành tích Đã kiểm chứng",
    en: "Verified Outcomes"
  },
  headline: {
    vi: "Nơi các kỹ sư nhí vươn mình thành nhà vô địch giải đấu.",
    en: "Where young builders become tournament champions."
  },
  supporting: {
    vi: "Học viên mang các dự án Robotics chế tạo tại phòng lab tranh tài tại đấu trường quốc tế và khẳng định bản lĩnh.",
    en: "Our students take their lab-built robotics to international arenas and come home with proof."
  },
  narrative: {
    vi: "Thi đấu Robotics đỉnh cao chưa bao giờ là sự may mắn. Tại ROBOTO, các em tự tay thiết kế hệ truyền động tự hành, tinh chỉnh vòng lặp cảm biến và lập trình phương án an toàn theo thời gian thực. Khi bước vào sàn đấu, các em tự tin như những kỹ sư thực thụ làm chủ các bài toán cơ khí và Engineering thực chiến.",
    en: "Competitive robotics is never about luck. At ROBOTO, children design autonomous drive trains, tune sensor loops, and program real-time failsafes. When they step onto the tournament floor, they operate with the quiet poise of trained engineers solving genuine mechanical challenges."
  },
  ctaTrial: {
    vi: "Đăng ký học thử miễn phí",
    en: "Book a free trial"
  },
  ctaTracks: {
    vi: "Khám phá các lộ trình thi đấu",
    en: "Explore competition tracks"
  }
};
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes"
};
export default function Highlights() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<Lang>("vi");
  useEffect(() => {
    const getStoredLang = (): Lang => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") return stored;
      } catch {
        // Fallback safely if localStorage is unavailable
      }
      return "vi";
    };
    setLang(getStoredLang());
    const handleLangChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        lang?: Lang;
      }>;
      const nextLang = customEvent.detail?.lang;
      if (nextLang === "vi" || nextLang === "en") {
        setLang(nextLang);
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "roboto_lang" && (event.newValue === "vi" || event.newValue === "en")) {
        setLang(event.newValue as Lang);
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);
  const containerMotion = {
    initial: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="achievement-highlights" data-nav="dark" className="dark bg-background text-foreground relative py-20 lg:py-24 border-t border-border overflow-hidden">
      {/* Background Lab Blueprint Coordinate Pattern */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(hsl(var(--color-secondary)/0.15)_1px,transparent_1px)] [background-size:32px_32px]" />

      {/* Decorative Static Floating Mascot */}
      <div aria-hidden="true" className="pointer-events-none select-none absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 lg:top-8 lg:right-12 xl:top-10 xl:right-16 z-0">
        <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-28 md:h-28 lg:w-36 lg:h-36 xl:w-40 xl:h-40 flex items-center justify-center bg-transparent">
          <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain bg-transparent pointer-events-none select-none filter drop-shadow-md" />
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Academy Trophy & Recognition Exhibit */}
          <motion.div {...containerMotion} className="lg:col-span-5 flex flex-col justify-center">
            <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
              {/* Precision Spec Rail Accent */}
              <div className="flex items-center gap-2 mb-3">
                <span className="h-1.5 w-1.5 rounded-xs bg-secondary" />
                <div className="h-[1px] w-16 bg-secondary" />
                <span className="font-mono text-xs uppercase tracking-widest text-secondary font-medium">
                  {content.specRail[lang]}
                </span>
              </div>

              {/* Main Image Surface */}
              <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-2xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_competition_trophy_cabinet.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_competition_trophy_cabinet.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_competition_trophy_cabinet.png-wvc-sizes" alt={content.imgAlt[lang]} loading="lazy" decoding="async" className="w-full h-[380px] sm:h-[460px] object-cover" />

                {/* Scrim Overlay for contrast & technical feel */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/25 to-transparent pointer-events-none" />

                {/* Overlaid Verified Certificate Badge */}
                <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 flex flex-col gap-2 rounded-lg border border-border/80 bg-background/95 p-4 backdrop-blur-md">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary/15 text-secondary">
                        <Trophy className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground">
                          {content.certDivision[lang]}
                        </p>
                        <p className="text-sm font-semibold text-foreground">
                          {content.certCertified[lang]}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="font-mono text-[10px] tracking-wider uppercase">
                      {content.certVerified[lang]}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Auxiliary Accreditation Indicator */}
              <div className="mt-4 flex items-center gap-2 text-muted-foreground text-xs font-mono">
                <ShieldCheck className="h-4 w-4 text-secondary shrink-0" />
                <span>{content.accreditation[lang]}</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Narrative & Competitive Program Focus */}
          <motion.div {...containerMotion} transition={{
          ...containerMotion.transition,
          delay: shouldReduceMotion ? 0 : 0.1
        }} className="lg:col-span-7 flex flex-col justify-center">
            {/* Stage Indicator / Eyebrow */}
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="rounded-full bg-secondary/10 px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider text-secondary border border-secondary/30">
                {content.eyebrow[lang]}
              </span>
            </div>

            {/* Section Headline */}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              {content.headline[lang]}
            </h2>

            {/* Supporting Line */}
            <p className="mt-4 text-lg sm:text-xl text-secondary font-medium leading-relaxed">
              {content.supporting[lang]}
            </p>

            {/* In-depth Narrative Body */}
            <p className="mt-3 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
              {content.narrative[lang]}
            </p>

            {/* Program Pillars Grid (Non-numeric) */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 pt-6 border-t border-border">
              {achievementTracks.map((track, i) => <div key={track.id} data-index={i} className="rounded-lg border border-border/80 bg-card/40 p-4 sm:p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      {i === 0 && <div className="flex h-9 w-9 items-center justify-center rounded-md bg-accent/15">
                          <Award className="h-5 w-5 text-accent shrink-0" />
                        </div>}
                      {i === 1 && <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary/15">
                          <Trophy className="h-5 w-5 text-secondary shrink-0" />
                        </div>}
                      {i === 2 && <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary/15">
                          <Globe className="h-5 w-5 text-secondary shrink-0" />
                        </div>}
                    </div>
                    <h3 className="mt-3 text-sm sm:text-base font-semibold text-foreground">
                      {track.title[lang]}
                    </h3>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                    {track.description[lang]}
                  </p>
                </div>)}
            </div>

            {/* Direct Conversion Actions */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-accent text-accent-foreground font-semibold hover:bg-accent/90">
                <Link to="/FreeTrialContactPage">
                  {content.ctaTrial[lang]}
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted/50">
                <Link to="/ProjectsCompetitionsPage">
                  <span>{content.ctaTracks[lang]}</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}